import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Wrench, Users, FileText, ClipboardList, Plus, Loader2, Search, CornerDownLeft, ListTodo, Check } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { StatusDot } from "@/components/StatusPill";
import { formatDate } from "@/lib/format";
import api from "@/lib/api";

/**
 * Global search (Ctrl/Cmd+K). Server-side search across machines, customers and reports.
 */
export const GlobalSearch = ({ open, onOpenChange }) => {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [res, setRes] = useState({ machines: [], customers: [], reports: [], tasks: [] });
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    if (!open) {
      setQ("");
      setRes({ machines: [], customers: [], reports: [], tasks: [] });
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const term = q.trim();
    if (timer.current) clearTimeout(timer.current);
    if (!term) {
      setRes({ machines: [], customers: [], reports: [], tasks: [] });
      setLoading(false);
      return;
    }
    setLoading(true);
    timer.current = setTimeout(() => {
      api
        .get(`/search`, { params: { q: term, limit: 6 } })
        .then((r) => setRes({ tasks: [], ...r.data }))
        .catch(() => {})
        .finally(() => setLoading(false));
    }, 220);
    return () => timer.current && clearTimeout(timer.current);
  }, [q, open]);

  const go = (path) => {
    onOpenChange(false);
    navigate(path);
  };

  const hasResults = res.machines.length + res.customers.length + res.reports.length + (res.tasks || []).length > 0;
  const term = q.trim();

  const actions = useMemo(
    () => [
      { label: "Ny servicerapport", icon: FileText, to: "/rapportar/ny-service", testid: "search-action-new-service" },
      { label: "Ny jobbrapport", icon: ClipboardList, to: "/rapportar/ny-jobb", testid: "search-action-new-jobb" },
      { label: "Ny maskin", icon: Wrench, to: "/maskiner?ny=1", testid: "search-action-new-machine" },
      { label: "Ny kunde", icon: Users, to: "/kunder?ny=1", testid: "search-action-new-customer" },
      { label: "Nytt punkt på huskelista", icon: ListTodo, to: "/huskeliste?ny=1", testid: "search-action-new-task" },
    ],
    []
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="top-[8%] max-w-xl translate-y-0 overflow-hidden p-0 sm:top-[14%]"
        data-testid="global-search-command-dialog"
      >
        <DialogTitle className="sr-only">Søk</DialogTitle>
        <Command shouldFilter={false} className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-[11px] [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider [&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group]]:px-2 [&_[cmdk-item]]:rounded-lg [&_[cmdk-item]]:px-2.5 [&_[cmdk-item]]:py-2.5">
          <CommandInput
            data-testid="global-search-input"
            placeholder="Søk maskin, kunde, serienr, maskinnr, rapportnr …"
            value={q}
            onValueChange={setQ}
            className="h-12 text-base"
          />
          <CommandList className="max-h-[60vh]">
            {loading && (
              <div className="flex items-center gap-2 px-4 py-3 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Søkjer …
              </div>
            )}
            {!loading && term && !hasResults && (
              <CommandEmpty data-testid="global-search-empty">
                Ingen treff. Prøv serienummer eller kundens maskinnr.
              </CommandEmpty>
            )}

            {res.machines.length > 0 && (
              <CommandGroup heading="Maskiner">
                {res.machines.map((m) => (
                  <CommandItem
                    key={m.id}
                    value={`m-${m.id}`}
                    onSelect={() => go(`/maskiner/${m.id}`)}
                    data-testid={`search-result-machine-${m.id}`}
                    className="cursor-pointer gap-3"
                  >
                    <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-muted"><Wrench className="h-4 w-4" /></span>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 truncate font-medium">
                        <StatusDot status={m.status} /> {m.label}
                      </div>
                      <div className="truncate text-xs text-muted-foreground">
                        {[m.customer_name, m.serial_number && `SN ${m.serial_number}`].filter(Boolean).join(" · ")}
                      </div>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            {res.customers.length > 0 && (
              <CommandGroup heading="Kundar">
                {res.customers.map((c) => (
                  <CommandItem
                    key={c.id}
                    value={`c-${c.id}`}
                    onSelect={() => go(`/maskiner?customer=${c.id}`)}
                    data-testid={`search-result-customer-${c.id}`}
                    className="cursor-pointer gap-3"
                  >
                    <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-muted"><Users className="h-4 w-4" /></span>
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-medium">{c.name}</div>
                      <div className="truncate text-xs text-muted-foreground">
                        {[c.contact_person, c.customer_type === "serviceavtale" ? "Serviceavtale" : "Engangskunde"].filter(Boolean).join(" · ")}
                      </div>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            {res.reports.length > 0 && (
              <CommandGroup heading="Rapportar">
                {res.reports.map((r) => (
                  <CommandItem
                    key={r.id}
                    value={`r-${r.id}`}
                    onSelect={() => go(`/rapportar?open=${r.id}`)}
                    data-testid={`search-result-report-${r.id}`}
                    className="cursor-pointer gap-3"
                  >
                    <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-muted">
                      {r.report_type === "jobb" ? <ClipboardList className="h-4 w-4" /> : <FileText className="h-4 w-4" />}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-medium">
                        {r.report_number} · {r.title || r.service_type || (r.report_type === "jobb" ? "Jobbrapport" : "Servicerapport")}
                      </div>
                      <div className="truncate text-xs text-muted-foreground">
                        {[formatDate(r.date), r.customer_snapshot?.name, [r.machine_snapshot?.brand, r.machine_snapshot?.type].filter(Boolean).join(" ")].filter(Boolean).join(" · ")}
                      </div>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            {(res.tasks || []).length > 0 && (
              <CommandGroup heading="Huskeliste">
                {res.tasks.map((t) => (
                  <CommandItem
                    key={t.id}
                    value={`t-${t.id}`}
                    onSelect={() => go(`/huskeliste?status=${t.status === "done" ? "done" : "open"}&q=${encodeURIComponent(t.title)}`)}
                    data-testid={`search-result-task-${t.id}`}
                    className="cursor-pointer gap-3"
                  >
                    <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-muted">
                      {t.status === "done" ? <Check className="h-4 w-4 text-[rgb(var(--status-ok))]" /> : <ListTodo className="h-4 w-4" />}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className={`truncate font-medium ${t.status === "done" ? "line-through text-muted-foreground" : ""}`}>{t.title}</div>
                      <div className="truncate text-xs text-muted-foreground">
                        {[t.status === "done" ? "Fullført" : "Open", t.priority === "hoeg" ? "Høg prioritet" : null, t.due_date ? `Ønska ${formatDate(t.due_date)}` : null, t.created_by_name].filter(Boolean).join(" · ")}
                      </div>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            {hasResults && <CommandSeparator />}
            <CommandGroup heading="Handlingar">
              {actions.map((a) => (
                <CommandItem key={a.to} value={a.to} onSelect={() => go(a.to)} data-testid={a.testid} className="cursor-pointer gap-3">
                  <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-[#9a2f00] dark:text-primary"><Plus className="h-4 w-4" /></span>
                  <span className="font-medium">{a.label}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
          <div className="hidden items-center justify-between border-t px-4 py-2 text-[11px] text-muted-foreground sm:flex">
            <span className="flex items-center gap-1"><Search className="h-3 w-3" /> Skriv for å søkje</span>
            <span className="flex items-center gap-1"><CornerDownLeft className="h-3 w-3" /> Opne · Esc for å lukke</span>
          </div>
        </Command>
      </DialogContent>
    </Dialog>
  );
};
